import { List } from 'immutable';

import { startsWithAlphanumeric, usedMoreThanAllowed } from '@/utils/validations/utils';
import { isValidNumber } from '@/utils/number';
import { getSize } from '@/utils/iterable/getSize';

import { ERRORS, PERSONALIZATION_CHARACTERS_COUNT_MIN, PERSONALIZATION_CHARACTERS_COUNT_MAX, PERSONALIZATION_INSTRUCTION_LENGTH_MAX, PERSONALIZATION_QUESTION_INSTRUCTIONS_LENGTH_MAX, PERSONALIZATION_QUESTION_LABEL_LENGTH_MAX, PERSONALIZATION_QUESTION_CHARACTER_LIMIT_MAX, PERSONALIZATION_QUESTION_OPTIONS_COUNT_MAX, PERSONALIZATION_QUESTION_OPTION_NAME_LENGTH_MAX, PERSONALIZATION_QUESTION_FILE_NAME_LENGTH_MAX } from '@/constants/validations';
import { ENABLED, PERSONALIZATION_TYPE, VALUE } from '@/constants/product';
import { ATTRIBUTE } from '@/constants/attributes';
import { FEATURE } from '@/constants/features';
import { EMPTY } from '@/constants';
import { ETSY } from '@/constants/channels';

export function getInstructionsError({ allowStartWithAnyCharacter, channel, value }) {
  if (!value.length) {
    return false;
  } else if (value.length > PERSONALIZATION_INSTRUCTION_LENGTH_MAX[channel]) {
    return ERRORS.PERSONALIZATION.LENGTH_EXCEEDED[channel];
  } else if (!allowStartWithAnyCharacter && !startsWithAlphanumeric(value)) {
    return ERRORS.PERSONALIZATION.ALPHANUMERIC_BEGIN;
  } else if (usedMoreThanAllowed({ patterns: [/\b\p{Lu}{3,}\b/], number: 1, value })) {
    return ERRORS.PERSONALIZATION.CAPITALIZED_WORDS_NUMBER_EXCEEDED;
  } else {
    return false;
  }
}

export function getCharactersCountError({ channel, value }) {
  return (
    (
      !isValidNumber(value) ||
      parseInt(value) !== parseFloat(value) ||
      value < PERSONALIZATION_CHARACTERS_COUNT_MIN[channel] ||
      value > PERSONALIZATION_CHARACTERS_COUNT_MAX[channel]
    ) && ERRORS.PERSONALIZATION.LIMIT_OUT_OF_RANGE[channel]
  );
}

function validatePersonalizationQuestion({ channel, question }) {
  function validateLabel({ errors }) {
    const label = question.get('label');

    if (!label) {
      return errors.set('label', ERRORS.PERSONALIZATION_QUESTIONS.LABEL.EMPTY);
    }

    if (!startsWithAlphanumeric(label)) {
      return errors.set('label', ERRORS.PERSONALIZATION_QUESTIONS.LABEL.ALPHANUMERIC_BEGIN);
    }

    if (getSize(label) > PERSONALIZATION_QUESTION_LABEL_LENGTH_MAX[ETSY]) {
      return errors.set('label', ERRORS.PERSONALIZATION_QUESTIONS.LABEL.LENGTH_EXCEEDED[ETSY]);
    }

    if (usedMoreThanAllowed({ patterns: [/\b\p{Lu}{3,}\b/], number: 1, value: label })) {
      return errors.set('label', ERRORS.PERSONALIZATION_QUESTIONS.LABEL.CAPITALIZED_WORDS);
    }

    return errors;
  }

  function validateInstructions({ errors }) {
    const instructions = question.get('instructions');

    if (!startsWithAlphanumeric(instructions)) {
      return errors.set('instructions', ERRORS.PERSONALIZATION_QUESTIONS.INSTRUCTIONS.ALPHANUMERIC_BEGIN);
    }

    if (getSize(instructions) > PERSONALIZATION_QUESTION_INSTRUCTIONS_LENGTH_MAX[ETSY]) {
      return errors.set('instructions', ERRORS.PERSONALIZATION_QUESTIONS.INSTRUCTIONS.LENGTH_EXCEEDED[ETSY]);
    }

    if (usedMoreThanAllowed({ patterns: [/\b\p{Lu}{3,}\b/], number: 1, value: instructions })) {
      return errors.set('instructions', ERRORS.PERSONALIZATION_QUESTIONS.INSTRUCTIONS.CAPITALIZED_WORDS);
    }

    return errors;
  }

  function validateOptions({ errors: source }) {
    const options = question.get('options');
    const optionsCount = getSize(options);
    let names = EMPTY.MAP;
    let errors = source;

    if (optionsCount === 0) {
      errors = errors.set('optionsCount', ERRORS.PERSONALIZATION_QUESTIONS.OPTIONS.NO_OPTIONS);
    } else {
      if (optionsCount > PERSONALIZATION_QUESTION_OPTIONS_COUNT_MAX[ETSY]) {
        errors = errors.set('optionsCount', ERRORS.PERSONALIZATION_QUESTIONS.OPTIONS.NUMBER_EXCEEDED[ETSY]);
      }

      for (const [optionIndex, option] of options.entries()) {
        const label = option.get('label');

        if (!label) {
          errors = errors.setIn(['options', optionIndex], ERRORS.PERSONALIZATION_QUESTIONS.OPTIONS.NO_NAME);
        } else {
          if (getSize(label) > PERSONALIZATION_QUESTION_OPTION_NAME_LENGTH_MAX[ETSY]) {
            errors = errors.setIn(['options', optionIndex], ERRORS.PERSONALIZATION_QUESTIONS.OPTIONS.LENGTH_EXCEEDED[ETSY]);
          }

          if (!names.has(label)) {
            names = names.set(label, List([optionIndex]));
          } else {
            for (const sameNameIndex of names.get(label).push(optionIndex).values()) {
              if (!errors.getIn(['options', sameNameIndex])) {
                errors = errors.setIn(['options', sameNameIndex], ERRORS.PERSONALIZATION_QUESTIONS.OPTIONS.NAME_EXIST);
              }
            }

            names = names.set(label, EMPTY.LIST);
          }
        }
      }
    }

    return errors;
  }

  function validateFiles({ errors: source }) {
    const maximum = question.get('maximum');
    const options = question.get('options');
    const optionsCount = getSize(options);
    let names = EMPTY.MAP;
    let errors = source;

    if (optionsCount < maximum) {
      errors = errors.set('filesCount', ERRORS.PERSONALIZATION_QUESTIONS.FILES.MINIMUM);
    } else if (optionsCount > maximum) {
      errors = errors.set('filesCount', ERRORS.PERSONALIZATION_QUESTIONS.FILES.MAXIMUM);
    }

    for (const [optionIndex, option] of options.entries()) {
      const label = option.get('label');

      if (!label) {
        errors = errors.setIn(['files', optionIndex], ERRORS.PERSONALIZATION_QUESTIONS.FILES.NO_NAME);
      } else {
        if (!startsWithAlphanumeric(label)) {
          errors = errors.setIn(['files', optionIndex], ERRORS.PERSONALIZATION_QUESTIONS.FILES.ALPHANUMERIC_BEGIN);
        } else if (getSize(label) > PERSONALIZATION_QUESTION_FILE_NAME_LENGTH_MAX[ETSY]) {
          errors = errors.setIn(['files', optionIndex], ERRORS.PERSONALIZATION_QUESTIONS.FILES.LENGTH_EXCEEDED[ETSY]);
        }

        if (!names.has(label)) {
          names = names.set(label, List([optionIndex]));
        } else {
          for (const sameNameIndex of names.get(label).push(optionIndex).values()) {
            if (!errors.getIn(['files', sameNameIndex])) {
              errors = errors.setIn(['files', sameNameIndex], ERRORS.PERSONALIZATION_QUESTIONS.FILES.NAME_EXIST);
            }
          }

          names = names.set(label, EMPTY.LIST);
        }
      }
    }

    return errors;
  }

  function validateCharacterLimit({ errors }) {
    const maximum = question.get('maximum');

    if (
      !Number.isFinite(maximum) ||
      maximum < 1 ||
      maximum > PERSONALIZATION_QUESTION_CHARACTER_LIMIT_MAX[ETSY]
    ) {
      return errors.set('maximum', ERRORS.PERSONALIZATION_QUESTIONS.CHARACTER_LIMIT[ETSY]);
    }

    return errors;
  }

  switch (channel) {
    case ETSY: {
      switch (question.get('type')) {
        case PERSONALIZATION_TYPE.FILES: {
          let errors = validateLabel({ errors: EMPTY.MAP });

          if (question.get('instructions')) {
            errors = validateInstructions({ errors });
          }

          if (question.get('labelled') && question.get('maximum') > 1) {
            errors = validateFiles({ errors });
          }

          return errors;
        }

        case PERSONALIZATION_TYPE.OPTIONS: {
          let errors = validateLabel({ errors: EMPTY.MAP });
          errors = validateOptions({ errors });
          return errors;
        }

        case PERSONALIZATION_TYPE.TEXT: {
          let errors = validateLabel({ errors: EMPTY.MAP });

          if (question.get('instructions')) {
            errors = validateInstructions({ errors });
          }

          errors = validateCharacterLimit({ errors });
          return errors;
        }

        default: {
          return EMPTY.MAP;
        }
      }
    }

    default: {
      return EMPTY.MAP;
    }
  }
}

export function validatePersonalization({ channel, personalization, userFeatures }) {
  if (!userFeatures.get(FEATURE.ETSY_PERSONALIZATION_QUESTIONS)) {
    let errors = EMPTY.MAP;

    if (personalization.get(ENABLED)) {
      const instructionsError = getInstructionsError({
        channel,
        value: personalization.getIn([ATTRIBUTE.INSTRUCTIONS, VALUE]),
      });

      if (instructionsError) {
        errors = errors.set(ATTRIBUTE.INSTRUCTIONS, instructionsError);
      }

      const charactersCountError = getCharactersCountError({
        channel,
        value: personalization.getIn([ATTRIBUTE.CHAR_COUNT_MAX, VALUE]),
      });

      if (charactersCountError) {
        errors = errors.set(ATTRIBUTE.CHAR_COUNT_MAX, charactersCountError);
      }
    }

    return errors;
  }

  function getFilesUploadError(errors) {
    let count = 0;

    for (const question of personalization.get(VALUE).values()) {
      if (question.get('type') === PERSONALIZATION_TYPE.FILES) {
        count += 1;

        if (count > 1) {
          return errors.set(PERSONALIZATION_TYPE.FILES, ERRORS.PERSONALIZATION_QUESTIONS.FILES.ONE_PER_LISTING);
        }
      }
    }

    return errors;
  }

  function reducePersonalizationQuestions(result, question, index) {
    const errors = validatePersonalizationQuestion({ channel, question });

    return getSize(errors) > 0
      ? result.set(index, errors)
      : result;
  }

  return getFilesUploadError(personalization.get(VALUE).reduce(reducePersonalizationQuestions, EMPTY.MAP));
}
